"use client";

import React from "react";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Bell, ArrowUpRight, ShoppingBag, Smartphone, Activity, LifeBuoy, CheckCircle2 } from "lucide-react";

/**
 * Re-digital — the signature app DASHBOARD mockup (the brand's core
 * differentiator per generated_homepage.png): greeting, current data balance
 * with a circular usage METER, an active plan row, a top-up row, and an app-style
 * bottom tab bar. Static / export-safe / claim-safe (illustrative balance only,
 * NOT live data). Pure dp-* dark styling — no glass/blur/gradient utilities.
 *
 * APP-INTENT INSTRUMENTATION (market-test-plan §3/§10 — the explicit feed into
 * EXPERIMENTS_AND_MEASUREMENT §8 app-demand decision): the usage meter fires
 * `usage_meter_interacted` (experiment `app_intent_usage_meter_v1`) and the
 * top-up row fires `topup_cta_clicked` (experiment `app_intent_topup_cta_v1`).
 * experiment_id is sent as a first-class AnalyticsEvent field. Best-effort +
 * consent-gated; the surfaces stay illustrative (no live data, no fake app).
 */
export default function DashboardMockup() {
  const { t } = useTranslations();
  const pct = 72;

  const fire = (event_name: string, experiment_id: string) => {
    try {
      track({ event_name, event_id: `${event_name}:${experiment_id}:${Date.now()}`, experiment_id });
    } catch {
      /* analytics is best-effort + consent-gated */
    }
  };

  return (
    <div className="dp-card rounded-[2rem] p-4 w-full max-w-[340px] mx-auto">
      {/* header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-foreground">{t("dp.dash.hello", "Hello, Traveler")}</div>
          <div className="text-xs text-muted-foreground">{t("dp.dash.welcome", "Welcome back")}</div>
        </div>
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full dp-chip text-foreground"><Bell className="w-4 h-4" /></span>
      </div>

      {/* balance + meter */}
      <div className="dp-card mt-4 p-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground">{t("dp.dash.balance", "Current balance")}</div>
          <div className="text-3xl font-extrabold text-foreground leading-none mt-1">24.6 <span className="text-base font-bold text-muted-foreground">GB</span></div>
          <div className="text-[11px] text-muted-foreground mt-2">{t("dp.dash.valid", "Valid until 12 Jun")}</div>
        </div>
        <button
          type="button"
          onClick={() => fire("usage_meter_interacted", "app_intent_usage_meter_v1")}
          aria-label={t("dp.dash.meterAria", "View data usage")}
          className="dp-meter relative w-16 h-16 flex items-center justify-center cursor-pointer"
          style={{ ["--pct" as string]: pct }}
        >
          <span className="absolute inset-[6px] rounded-full bg-card flex flex-col items-center justify-center">
            <span className="text-sm font-extrabold text-foreground leading-none">{pct}%</span>
            <span className="text-[8px] text-muted-foreground">{t("dp.dash.used", "Used")}</span>
          </span>
        </button>
      </div>

      {/* active plan */}
      <div className="dp-card mt-3 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg dp-chip text-primary"><Activity className="w-4 h-4" /></span>
          <div>
            <div className="text-sm font-bold text-foreground">{t("dp.dash.plan", "Asia Explorer")}</div>
            <div className="text-[11px] text-muted-foreground">{t("dp.dash.countries", "15 countries")}</div>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-success"><CheckCircle2 className="w-3.5 h-3.5" />{t("dp.dash.active", "Active")}</span>
      </div>

      {/* top up */}
      <button
        type="button"
        onClick={() => fire("topup_cta_clicked", "app_intent_topup_cta_v1")}
        className="dp-card mt-3 p-3 w-full flex items-center justify-between text-left cursor-pointer"
      >
        <div>
          <div className="text-sm font-bold text-foreground">{t("dp.dash.topup", "Top up data")}</div>
          <div className="text-[11px] text-muted-foreground">{t("dp.dash.topupB", "Add more data to stay connected")}</div>
        </div>
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full dp-pill"><ArrowUpRight className="w-4 h-4" /></span>
      </button>

      {/* tab bar */}
      <div className="mt-4 grid grid-cols-4 gap-1 border-t border-border pt-3">
        {[
          { icon: ShoppingBag, l: t("dp.dash.tab1", "Buy") },
          { icon: Smartphone, l: t("dp.dash.tab2", "My eSIMs") },
          { icon: Activity, l: t("dp.dash.tab3", "Usage") },
          { icon: LifeBuoy, l: t("dp.dash.tab4", "Support") },
        ].map(({ icon: Icon, l }, i) => (
          <div key={i} className="flex flex-col items-center gap-1 text-[10px] text-muted-foreground">
            <Icon className={`w-4 h-4 ${i === 0 ? "text-primary" : ""}`} />{l}
          </div>
        ))}
      </div>
    </div>
  );
}
